import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const AnnouncementsPanel = ({ announcements, onMarkAsRead }) => {
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const priorityColors = {
    urgent: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    high: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20',
    normal: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
    low: 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20'
  };

  const priorityIcons = {
    urgent: 'AlertTriangle',
    high: 'AlertCircle',
    normal: 'Info',
    low: 'Bell'
  };

  const categoryIcons = {
    general: 'Info',
    class: 'Calendar',
    maintenance: 'Wrench',
    event: 'PartyPopper',
    policy: 'FileText',
    emergency: 'AlertTriangle'
  };

  const filteredAnnouncements = announcements?.filter(announcement => {
    if (filterPriority !== 'all' && announcement?.priority !== filterPriority) return false;
    if (filterCategory !== 'all' && announcement?.category !== filterCategory) return false;
    return true;
  });

  const handleAnnouncementClick = (announcement) => {
    setSelectedAnnouncement(announcement);
    onMarkAsRead(announcement?.id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date?.toLocaleDateString();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Announcements List */}
      <div className="lg:col-span-1 space-y-4">
        {/* Filters */}
        <div className="bg-card rounded-xl p-4 shadow-md border border-border space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
              Priority
            </label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Categories</option>
              <option value="general">General</option>
              <option value="class">Class</option>
              <option value="maintenance">Maintenance</option>
              <option value="event">Event</option>
              <option value="policy">Policy</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>
        </div>

        {/* Announcements List */}
        <div className="space-y-3">
          {filteredAnnouncements?.length === 0 ? (
            <div className="bg-card rounded-xl p-8 shadow-md border border-border text-center">
              <Icon name="Megaphone" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No announcements found</p>
            </div>
          ) : (
            filteredAnnouncements?.map((announcement) => (
              <button
                key={announcement?.id}
                onClick={() => handleAnnouncementClick(announcement)}
                className={`
                  w-full text-left bg-card rounded-xl p-4 shadow-md border transition-all
                  ${selectedAnnouncement?.id === announcement?.id
                    ? 'border-primary ring-2 ring-primary/20' :'border-border hover:border-primary/50'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${priorityColors?.[announcement?.priority]}`}>
                    <Icon name={priorityIcons?.[announcement?.priority]} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground text-sm mb-1 truncate">
                      {announcement?.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name={categoryIcons?.[announcement?.category]} size={14} />
                      <span className="capitalize">{announcement?.category}</span>
                      <span>•</span>
                      <span>{formatDate(announcement?.published_at)}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
      {/* Announcement Detail */}
      <div className="lg:col-span-2">
        {selectedAnnouncement ? (
          <div className="bg-card rounded-xl p-6 shadow-md border border-border">
            <div className="flex items-start gap-4 mb-6">
              <div className={`p-3 rounded-xl ${priorityColors?.[selectedAnnouncement?.priority]}`}>
                <Icon name={priorityIcons?.[selectedAnnouncement?.priority]} size={28} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${priorityColors?.[selectedAnnouncement?.priority]}`}>
                    {selectedAnnouncement?.priority}
                  </span>
                  <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium capitalize">
                    {selectedAnnouncement?.category}
                  </span>
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {selectedAnnouncement?.title}
                </h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="User" size={16} />
                  <span>{selectedAnnouncement?.created_by_profile?.full_name || 'Staff'}</span>
                  <span>•</span>
                  <Icon name="Calendar" size={16} />
                  <span>{new Date(selectedAnnouncement?.published_at)?.toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="prose prose-sm max-w-none">
              <p className="text-foreground whitespace-pre-wrap">{selectedAnnouncement?.content}</p>
            </div>

            {selectedAnnouncement?.expires_at && (
              <div className="mt-6 p-4 bg-muted rounded-lg flex items-center gap-3">
                <Icon name="Clock" size={20} color="var(--color-muted-foreground)" />
                <div>
                  <p className="text-sm font-medium text-foreground">Expires</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(selectedAnnouncement?.expires_at)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-card rounded-xl p-12 shadow-md border border-border h-full flex flex-col items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <Icon name="Megaphone" size={40} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No Announcement Selected
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Select an announcement from the list to view its details
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementsPanel;